import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Briefcase,
  TrendingUp,
  History,
  Radio,
  Database,
  Lightbulb,
  FileText,
  Bell,
  ShieldAlert,
  FolderTree,
  Compass,
  Octagon,
  Crown,
  type LucideIcon,
} from 'lucide-react';

// 권위: docs/ARCH_DASHBOARD.md §4.0.5 좌측 Sidebar
type NavItem = {
  to: string;
  label: string;
  end?: boolean;
  icon?: LucideIcon;
  children?: NavItem[];
};

type NavGroup = {
  label: string;
  items: NavItem[];
};

// 운영자 사용 빈도 기준 4-그룹 구조.
const NAV_GROUPS: NavGroup[] = [
  {
    label: '분석 & 의사결정',
    items: [
      {
        to: '/governance/live',
        label: '거버넌스',
        icon: Compass,
        children: [
          { to: '/governance/live', label: '실시간 현황' },
          { to: '/governance/sla', label: 'SLA 감시' },
          { to: '/governance/stage-override', label: 'Stage 재정의' },
        ],
      },
      {
        to: '/leading/sectors',
        label: '주도주',
        icon: Crown,
        children: [
          { to: '/leading/sectors', label: '주도 섹터 시계열' },
          { to: '/leading/stocks', label: 'LeaderStocks45' },
          { to: '/leading/scores', label: 'Stock Score Explorer' },
        ],
      },
      {
        to: '/sector-intelligence',
        label: '섹터 인텔리전스',
        icon: FolderTree,
        children: [
          { to: '/sector-intelligence/sectors', label: '섹터' },
          { to: '/sector-intelligence/stocks', label: '종목' },
          { to: '/sectors/taxonomy', label: '택소노미 관리' },
          { to: '/sectors/insights', label: '섹터 인사이트' },
        ],
      },
      { to: '/signals', label: '시그널·예측', icon: TrendingUp },
      { to: '/live', label: '실시간 현황 (시스템 총괄)', icon: Radio },
    ],
  },
  {
    label: '운영 모니터링',
    items: [
      { to: '/', label: '시스템 개요', end: true, icon: LayoutDashboard },
      { to: '/risk/halt', label: 'Risk Halt 상태', icon: Octagon },
      {
        to: '/positions',
        label: '트레이딩',
        icon: Briefcase,
        children: [
          { to: '/positions', label: '포지션 현황' },
          { to: '/trading/live', label: '실시간 시그널 (트레이딩)' },
          { to: '/trading/unfilled', label: '미체결 큐' },
          { to: '/trading/events', label: '이벤트 audit' },
          { to: '/trading/backtest', label: '백테스트' },
        ],
      },
      { to: '/history', label: '실행 이력', icon: History },
    ],
  },
  {
    label: '데이터 관리',
    items: [
      {
        to: '/data',
        label: '데이터 탐색',
        icon: Database,
        children: [
          { to: '/data/disclosures', label: '공시' },
          { to: '/data/market-data-status', label: 'Morning Market Data' },
        ],
      },
      { to: '/pending-ca-events', label: 'Pending CA', icon: ShieldAlert },
      { to: '/logs', label: '시스템 로그', icon: FileText },
    ],
  },
  {
    label: '설정 & 참고',
    items: [
      { to: '/strategy', label: '전략 인사이트', icon: Lightbulb },
      { to: '/settings/alerts', label: '알림 설정', icon: Bell },
    ],
  },
];

interface SidebarProps {
  /** 모바일 drawer 열림 여부. 데스크톱에서는 무시 (항상 표시). */
  open?: boolean;
}

export function Sidebar({ open = false }: SidebarProps = {}): JSX.Element {
  return (
    <aside
      id="app-sidebar"
      className={`app-shell__sidebar${open ? ' app-shell__sidebar--open' : ''}`}
      aria-label="주 메뉴"
      aria-hidden={!open ? undefined : false}
    >
      <nav>
        {NAV_GROUPS.map((group, groupIdx) => (
          <div
            key={group.label}
            className={`sidebar-nav__group${
              groupIdx > 0 ? ' sidebar-nav__group--divided' : ''
            }`}
          >
            <div className="sidebar-nav__group-header">{group.label}</div>
            <ul className="sidebar-nav">
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        `sidebar-nav__link${isActive ? ' sidebar-nav__link--active' : ''}`
                      }
                    >
                      {Icon && <Icon size={16} strokeWidth={1.75} aria-hidden />}
                      <span>{item.label}</span>
                    </NavLink>
                    {item.children && item.children.length > 0 && (
                      <ul className="sidebar-nav sidebar-nav--child">
                        {item.children.map((child) => (
                          <li key={child.to}>
                            <NavLink
                              to={child.to}
                              end={child.end}
                              className={({ isActive }) =>
                                `sidebar-nav__link sidebar-nav__link--child${
                                  isActive ? ' sidebar-nav__link--active' : ''
                                }`
                              }
                            >
                              {child.label}
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  );
}
